import React, { useState } from 'react'
import QuizContent from '../shared/QuizContent'
import LazyImage from '../shared/LazyImage'
import GoogleIcon from '../../assets/Icon.png'
import { requestPasswordReset, updatePassword, verifyPasswordOtp } from '../../utils/api'
import { useTranslation } from 'react-i18next'
import { trackForgotPassCompleted } from '../../utils/analytics'

const ForgotPassword = ({ onContinue, onBackToLogin }) => {
  const { t } = useTranslation()

  const [step, setStep] = useState('forgot')
  const [showPassword, setShowPassword] = useState(false)
  const [showConfirmPassword, setShowConfirmPassword] = useState(false)
  const [email, setEmail] = useState('')
  const [otp, setOtp] = useState(['', '', '', '', ''])
  const [token, setToken] = useState('')
  const [newPassword, setNewPassword] = useState('')
  const [confirmPassword, setConfirmPassword] = useState('')
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState('')
  const [toastMessage, setToastMessage] = useState('')

  const showToast = message => {
    setToastMessage(message)
    setTimeout(() => {
      setToastMessage('')
      onBackToLogin()
    }, 2000)
  }

  const handleSendCode = async () => {
    if (!email) return setError(t('please_check'))
    setLoading(true)
    setError('')
    try {
      await requestPasswordReset(email)
      setStep('verify')
    } catch (err) {
      setError(err.message)
    } finally {
      setLoading(false)
    }
  }

  const handleVerifyCode = async () => {
    const code = otp.join('')
    if (code.length !== 5) return setError(t('enter_5code'))
    setLoading(true)
    setError('')
    try {
      const data = await verifyPasswordOtp(email, code)
      setToken(data.token)
      setStep('newPassword')
    } catch (err) {
      setError(err.message)
    } finally {
      setLoading(false)
    }
  }

  const handleUpdatePassword = async () => {
    if (!newPassword) return setError(t('enter_new_password'))
    if (confirmPassword !== newPassword) return setError(t('password_not_match'))
    setLoading(true)
    setError('')
    try {
      await updatePassword(token, newPassword)
      trackForgotPassCompleted()
      showToast(t('password_update_success'))
    } catch (err) {
      setError(err.message)
    } finally {
      setLoading(false)
    }
  }

  const handleOtpChange = (index, value) => {
    if (/^\d?$/.test(value)) {
      const newOtp = [...otp]
      newOtp[index] = value
      setOtp(newOtp)

      if (value && index < otp.length - 1) {
        const nextInput = document.querySelector(`.otp-inputs input:nth-child(${index + 2})`)
        if (nextInput) nextInput.focus()
      }
    }
  }

  const handleOtpKeyDown = (index, e) => {
    if (e.key === 'Backspace' && !otp[index] && index > 0) {
      const prevInput = document.querySelector(`.otp-inputs input:nth-child(${index})`)
      if (prevInput) prevInput.focus()
    }
  }

  const handleOtpPaste = e => {
    e.preventDefault()
    const pastedData = e.clipboardData.getData('text').replace(/\D/g, '')
    if (!pastedData) return

    const newOtp = [...otp]
    for (let i = 0; i < otp.length; i++) {
      newOtp[i] = pastedData[i] || ''
    }
    setOtp(newOtp)

    const lastFilledIndex = Math.min(pastedData.length, otp.length) - 1
    const nextInput = document.querySelector(`.otp-inputs input:nth-child(${lastFilledIndex + 1})`)
    if (nextInput) nextInput.focus()
  }

  return (
    <QuizContent className='account-page forgot'>
      <div className='account-container'>
        <div className='account-form-wrapper'>
          <div className='form-header'>
            <h2 className='account-title'>
              {step === 'forgot' ? t('forgot_password') : step === 'verify' ? t('verify_code') : t('set_new_password')}
            </h2>
            <p className='account-subtitle'>
              {step === 'forgot' && t('enter_email')}
              {step === 'verify' && t('enter_code')}
              {step === 'newPassword' && t('enter_your_new_password')}
            </p>
            <button className='close-button' onClick={() => onContinue(17)}>
              ×
            </button>
          </div>
          <form onSubmit={e => e.preventDefault()} className='account-form'>
            {step === 'forgot' && (
              <>
                <div className='input-wrapper'>
                  <input type='email' placeholder={t('enter_email2')} className='form-input' value={email} onChange={e => setEmail(e.target.value)} />
                </div>
                {error && <div className='error-message'>{error}</div>}

                <button type='button' className='submit-button' onClick={handleSendCode} disabled={loading}>
                  {loading ? t('sending') : t('send_code')}
                </button>

                <div onClick={onBackToLogin} className='login-link'>
                  {t('back_to')}{' '}
                  <button type='button' className='link-button'>
                    {t('log_in')} &gt;
                  </button>
                </div>
              </>
            )}

            {step === 'verify' && (
              <>
                <div className='otp-inputs'>
                  {otp.map((digit, index) => (
                    <input
                      key={index}
                      className='verifyCodeInput'
                      type='text'
                      maxLength={1}
                      value={digit}
                      onChange={e => handleOtpChange(index, e.target.value)}
                      onKeyDown={e => handleOtpKeyDown(index, e)}
                      onPaste={handleOtpPaste}
                    />
                  ))}
                </div>
                {error && <div className='error-message'>{error}</div>}

                <button type='button' className='submit-button' onClick={handleVerifyCode} disabled={loading}>
                  {loading ? t('verifying') : t('apply_code')}
                </button>

                <div className='login-link'>
                  <button type='button' className='link-button' onClick={handleSendCode}>
                    {t('resend_code')}
                  </button>
                </div>
              </>
            )}

            {step === 'newPassword' && (
              <>
                <div className='inputWrapper'>
                  <input
                    type={showPassword ? 'text' : 'password'}
                    placeholder={t('new_password')}
                    className='form-input'
                    value={newPassword}
                    onChange={e => setNewPassword(e.target.value)}
                  />{' '}
                  <button
                    type='button'
                    className='cardStep15InputSvg'
                    onClick={() => setShowPassword(!showPassword)}
                    style={{
                      background: 'none',
                      border: 'none',
                      cursor: 'pointer',
                      padding: 0,
                    }}
                    aria-label={showPassword ? t('hide_password') : t('show_password')}
                  >
                    {showPassword ? (
                      <svg xmlns='http://www.w3.org/2000/svg' width='16' height='16' viewBox='0 0 16 16' fill='none'>
                        <path
                          d='M8 3.33333C11.4036 3.33333 13.6369 6.33656 14.3871 7.52455C14.4779 7.66833 14.5233 7.74023 14.5488 7.85112C14.5678 7.93439 14.5678 8.06578 14.5487 8.14905C14.5233 8.25993 14.4776 8.3323 14.3861 8.47705C14.1862 8.79343 13.8814 9.23807 13.4777 9.7203C12.7169 10.6667 11.4036 12.6667 8 12.6667C4.59644 12.6667 2.36319 9.66344 1.61292 8.47545C1.52212 8.33168 1.47672 8.25979 1.45131 8.14891C1.43221 8.06563 1.43222 7.93426 1.45133 7.85099C1.47675 7.74011 1.52236 7.66791 1.61357 7.52352C2.36369 6.33656 4.59694 3.33333 8 3.33333Z'
                          stroke='#9B9CAA'
                          strokeWidth='1.5'
                          strokeLinecap='round'
                          strokeLinejoin='round'
                        />
                        <path
                          d='M8 6C9.10457 6 10 6.89543 10 8C10 9.10457 9.10457 10 8 10C6.89543 10 6 9.10457 6 8C6 6.89543 6.89543 6 8 6Z'
                          stroke='#9B9CAA'
                          strokeWidth='1.5'
                          strokeLinecap='round'
                          strokeLinejoin='round'
                        />
                      </svg>
                    ) : (
                      <svg xmlns='http://www.w3.org/2000/svg' width='16' height='16' viewBox='0 0 16 16' fill='none'>
                        <path
                          d='M7.16196 3.39488C7.4329 3.35482 7.7124 3.33333 8.00028 3.33333C11.4036 3.33333 13.6369 6.33656 14.3871 7.52455C14.4779 7.66833 14.5233 7.74023 14.5488 7.85112C14.5678 7.93439 14.5678 8.06578 14.5487 8.14905C14.5233 8.25993 14.4776 8.3323 14.3861 8.47705C14.1862 8.79343 13.8814 9.23807 13.4777 9.7203M4.48288 4.47669C3.0415 5.45447 2.06297 6.81292 1.61407 7.52352C1.52286 7.66791 1.47725 7.74011 1.45183 7.85099C1.43273 7.93426 1.43272 8.06563 1.45181 8.14891C1.47722 8.25979 1.52262 8.33168 1.61342 8.47545C2.36369 9.66344 4.59694 12.6667 8.00028 12.6667C9.37255 12.6667 10.5546 12.1784 11.5259 11.5177M2.00028 2L14.0003 14M6.58606 6.58579C6.22413 6.94772 6.00028 7.44772 6.00028 8C6.00028 9.10457 6.89571 10 8.00028 10C8.55256 10 9.05256 9.77614 9.41449 9.41421'
                          stroke='#9B9CAA'
                          strokeWidth='1.5'
                          strokeLinecap='round'
                          strokeLinejoin='round'
                        />
                      </svg>
                    )}
                  </button>
                </div>
                <div className='inputWrapper'>
                  <input
                    type={showConfirmPassword ? 'text' : 'password'}
                    placeholder={t('confirm_password')}
                    className='form-input confirm'
                    value={confirmPassword}
                    onChange={e => setConfirmPassword(e.target.value)}
                  />{' '}
                  <button
                    type='button'
                    className='cardStep15InputSvg'
                    onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                    style={{
                      background: 'none',
                      border: 'none',
                      cursor: 'pointer',
                      padding: 0,
                    }}
                    aria-label={showConfirmPassword ? t('hide_password') : t('show_password')}
                  >
                    {showConfirmPassword ? (
                      <svg xmlns='http://www.w3.org/2000/svg' width='16' height='16' viewBox='0 0 16 16' fill='none'>
                        <path
                          d='M8 3.33333C11.4036 3.33333 13.6369 6.33656 14.3871 7.52455C14.4779 7.66833 14.5233 7.74023 14.5488 7.85112C14.5678 7.93439 14.5678 8.06578 14.5487 8.14905C14.5233 8.25993 14.4776 8.3323 14.3861 8.47705C14.1862 8.79343 13.8814 9.23807 13.4777 9.7203C12.7169 10.6667 11.4036 12.6667 8 12.6667C4.59644 12.6667 2.36319 9.66344 1.61292 8.47545C1.52212 8.33168 1.47672 8.25979 1.45131 8.14891C1.43221 8.06563 1.43222 7.93426 1.45133 7.85099C1.47675 7.74011 1.52236 7.66791 1.61357 7.52352C2.36369 6.33656 4.59694 3.33333 8 3.33333Z'
                          stroke='#9B9CAA'
                          strokeWidth='1.5'
                          strokeLinecap='round'
                          strokeLinejoin='round'
                        />
                        <path
                          d='M8 6C9.10457 6 10 6.89543 10 8C10 9.10457 9.10457 10 8 10C6.89543 10 6 9.10457 6 8C6 6.89543 6.89543 6 8 6Z'
                          stroke='#9B9CAA'
                          strokeWidth='1.5'
                          strokeLinecap='round'
                          strokeLinejoin='round'
                        />
                      </svg>
                    ) : (
                      <svg xmlns='http://www.w3.org/2000/svg' width='16' height='16' viewBox='0 0 16 16' fill='none'>
                        <path
                          d='M7.16196 3.39488C7.4329 3.35482 7.7124 3.33333 8.00028 3.33333C11.4036 3.33333 13.6369 6.33656 14.3871 7.52455C14.4779 7.66833 14.5233 7.74023 14.5488 7.85112C14.5678 7.93439 14.5678 8.06578 14.5487 8.14905C14.5233 8.25993 14.4776 8.3323 14.3861 8.47705C14.1862 8.79343 13.8814 9.23807 13.4777 9.7203M4.48288 4.47669C3.0415 5.45447 2.06297 6.81292 1.61407 7.52352C1.52286 7.66791 1.47725 7.74011 1.45183 7.85099C1.43273 7.93426 1.43272 8.06563 1.45181 8.14891C1.47722 8.25979 1.52262 8.33168 1.61342 8.47545C2.36369 9.66344 4.59694 12.6667 8.00028 12.6667C9.37255 12.6667 10.5546 12.1784 11.5259 11.5177M2.00028 2L14.0003 14M6.58606 6.58579C6.22413 6.94772 6.00028 7.44772 6.00028 8C6.00028 9.10457 6.89571 10 8.00028 10C8.55256 10 9.05256 9.77614 9.41449 9.41421'
                          stroke='#9B9CAA'
                          strokeWidth='1.5'
                          strokeLinecap='round'
                          strokeLinejoin='round'
                        />
                      </svg>
                    )}
                  </button>
                </div>
                {error && <div className='error-message'>{error}</div>}

                <button type='button' className='submit-button' onClick={handleUpdatePassword} disabled={loading}>
                  {loading ? t('updating') : t('update_password')}
                </button>
              </>
            )}
          </form>{' '}
          {step === 'newPassword' && toastMessage && (
            <div className='toast-notification'>
              <svg xmlns='http://www.w3.org/2000/svg' fill='none' viewBox='0 0 24 24' strokeWidth={3} stroke='white' className='w-5 h-5'>
                <path strokeLinecap='round' strokeLinejoin='round' d='M4.5 12.75l6 6 9-13.5' />
              </svg>
              {toastMessage}
            </div>
          )}
        </div>
      </div>
    </QuizContent>
  )
}

export default ForgotPassword
